/**
 * The execution ledger: what the user actually performed, as opposed to what the
 * plan asked for.
 *
 * One rule governs every function here: **the ledger only ever grows.** Skipping
 * an interval banks the time genuinely spent in it and grants nothing more
 * (F-TR-09); stepping back re-opens a step without subtracting a millisecond from
 * it or clearing an id it already earned (F-TR-11). That is what makes "completed"
 * a claim the user earned rather than a default, and it is why the ledger is
 * cursor-independent — it may legitimately exceed the plan's duration when a user
 * revisits work, and the summary would rather over-report real effort than
 * silently delete it.
 *
 * The ledger is folded from `EngineEvent`s and never re-derives timing. `tick.ts`
 * already stamped every event at the instant it was reached; recomputing duration
 * here from a cursor would throw that correction away the moment a tab was
 * throttled.
 *
 * Two timelines are in play and must not be confused. Events carry *wall*
 * instants. `ExecutionVisit` offsets are measured into the session's **active**
 * duration, because a paused clock opens a hole in wall time that later visits
 * would appear to overlap. The active offset of a visit is therefore the ledger's
 * own performed total at the moment the time was banked — which is exactly the
 * cursor this fold carries.
 */

import { deriveExecutionEntryId, reconcileExecutionEntries } from "@ff/domain";
import type {
  EffortSessionId,
  ExecutionDisposition,
  ExecutionEntry,
  ExecutionGuidance,
  ExecutionVisit,
  Milliseconds,
  SessionSegment,
  SessionSegmentId,
  WorkoutExecutionLedger,
} from "@ff/domain";

import type { EngineEvent } from "./tick";

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/**
 * One planned step's own ledger.
 *
 * Keyed by plan position rather than by segment id because that is what the
 * archive's deterministic entry id is derived from (`{session}|current-step|
 * {planIndex}|{segmentId}`), and because a plan may legitimately contain the same
 * authored interval twice — two occurrences that must not collapse into one row.
 *
 * `completed` and `skipped` are two independent flags rather than one disposition
 * because both can be true of the same step: a user may skip a round, step back
 * into it, and then run it out. Neither flag ever clears, so the record keeps
 * saying the round was abandoned once — which is the honest reading, and the one
 * `normalizeEffortOutcome` applies when it refuses `completed` to a segment with a
 * skipped entry.
 */
export interface ExecutionStepLedger {
  readonly planIndex: number;
  readonly segmentId: SessionSegmentId;
  readonly performed: Milliseconds;
  /** Offsets into active duration, ascending and non-overlapping by construction. */
  readonly visits: readonly ExecutionVisit[];
  readonly completed: boolean;
  readonly skipped: boolean;
  /** What the engine was calling while the step was performed, when it was known. */
  readonly guidance?: ExecutionGuidance;
}

/**
 * The engine's running ledger.
 *
 * It *is* a `WorkoutExecutionLedger` — the checkpoint shape — with the per-step
 * detail the archive needs bolted on. Extending rather than wrapping means a
 * checkpoint write can pass this value straight through with no lossy projection
 * step to get wrong, while `steps` carries the visit structure that the
 * checkpoint's flat `performedBySegment` cannot express.
 */
export interface ExecutionLedger extends WorkoutExecutionLedger {
  readonly steps: readonly ExecutionStepLedger[];
  /**
   * The plan position whose visit is still open, if any.
   *
   * It exists because a visit boundary cannot be inferred from the numbers alone:
   * a Back that restarts the current interval re-enters the same step with no
   * other step's time in between, so the new occupancy begins exactly where the
   * old one ended and would otherwise be indistinguishable from a continuation.
   * The engine says which it is — `segmentBegan` — and this field is where that
   * fact is held between batches. It is engine state, not record state, and the
   * checkpoint neither stores nor needs it.
   */
  readonly openStepIndex?: number;
}

export const EMPTY_LEDGER: ExecutionLedger = Object.freeze({
  performedDuration: 0,
  performedBySegment: Object.freeze({}),
  completedSegmentIds: Object.freeze([]),
  skippedSegmentIds: Object.freeze([]),
  steps: Object.freeze([]),
});

// ---------------------------------------------------------------------------
// The fold
// ---------------------------------------------------------------------------

interface StepDraft {
  readonly planIndex: number;
  readonly segmentId: SessionSegmentId;
  performed: Milliseconds;
  visits: ExecutionVisit[];
  completed: boolean;
  skipped: boolean;
  guidance?: ExecutionGuidance;
}

interface LedgerDraft {
  performedDuration: Milliseconds;
  openStepIndex: number | undefined;
  readonly performedBySegment: Record<SessionSegmentId, Milliseconds>;
  readonly completedSegmentIds: SessionSegmentId[];
  readonly skippedSegmentIds: SessionSegmentId[];
  readonly order: StepDraft[];
  readonly byPlanIndex: Map<number, StepDraft>;
}

function draftFrom(ledger: ExecutionLedger): LedgerDraft {
  const order = ledger.steps.map((step) => ({
    planIndex: step.planIndex,
    segmentId: step.segmentId,
    performed: step.performed,
    visits: step.visits.map((visit) => ({ ...visit })),
    completed: step.completed,
    skipped: step.skipped,
    ...(step.guidance !== undefined ? { guidance: step.guidance } : {}),
  }));
  return {
    performedDuration: ledger.performedDuration,
    openStepIndex: ledger.openStepIndex,
    performedBySegment: { ...ledger.performedBySegment },
    completedSegmentIds: [...ledger.completedSegmentIds],
    skippedSegmentIds: [...ledger.skippedSegmentIds],
    order,
    byPlanIndex: new Map(order.map((step) => [step.planIndex, step])),
  };
}

function sealDraft(draft: LedgerDraft): ExecutionLedger {
  return {
    performedDuration: draft.performedDuration,
    performedBySegment: draft.performedBySegment,
    completedSegmentIds: draft.completedSegmentIds,
    skippedSegmentIds: draft.skippedSegmentIds,
    ...(draft.openStepIndex !== undefined ? { openStepIndex: draft.openStepIndex } : {}),
    steps: draft.order.map((step) => ({
      planIndex: step.planIndex,
      segmentId: step.segmentId,
      performed: step.performed,
      visits: step.visits,
      completed: step.completed,
      skipped: step.skipped,
      ...(step.guidance !== undefined ? { guidance: step.guidance } : {}),
    })),
  };
}

function stepDraft(draft: LedgerDraft, planIndex: number, segmentId: SessionSegmentId): StepDraft {
  const existing = draft.byPlanIndex.get(planIndex);
  if (existing !== undefined) return existing;
  const created: StepDraft = {
    planIndex,
    segmentId,
    performed: 0,
    visits: [],
    completed: false,
    skipped: false,
  };
  draft.order.push(created);
  draft.byPlanIndex.set(planIndex, created);
  return created;
}

/**
 * Banks measured time against a step at the current active-timeline cursor.
 *
 * A run of `performed` events inside one occupancy is one visit, not one visit per
 * display tick — a five-minute round would otherwise archive twelve hundred spans
 * describing a single unbroken effort. The occupancy ends when the engine says it
 * ended, which is why the open step is tracked rather than inferred: `open` is the
 * step whose visit is still accepting time.
 */
function bankPerformed(
  step: StepDraft,
  open: boolean,
  cursor: Milliseconds,
  duration: Milliseconds,
): void {
  const last = step.visits[step.visits.length - 1];
  if (open && last !== undefined) {
    step.visits[step.visits.length - 1] = {
      offsetMilliseconds: last.offsetMilliseconds,
      durationMilliseconds: last.durationMilliseconds + duration,
    };
    return;
  }
  step.visits.push({ offsetMilliseconds: cursor, durationMilliseconds: duration });
}

function record(ids: SessionSegmentId[], id: SessionSegmentId): void {
  if (!ids.includes(id)) ids.push(id);
}

/**
 * Folds a batch of engine events into the ledger.
 *
 * Returns the ledger unchanged — by identity — when the batch says nothing about
 * execution, so a display tick that only re-anchored the clock does not invalidate
 * every consumer memoized on this value.
 *
 * Note what is *not* handled: there is no case that removes an id, shortens a
 * visit or subtracts from a total, and `tick.ts` deliberately emits no
 * `segmentEnded` for a Back. Monotonicity is therefore a property of this
 * function's shape rather than a rule it has to remember to enforce.
 */
export function ledgerAfter(
  ledger: ExecutionLedger,
  events: readonly EngineEvent[],
): ExecutionLedger {
  let changed = false;
  const draft = draftFrom(ledger);

  for (const event of events) {
    switch (event.type) {
      case "performed": {
        // Truncated and floored defensively: a fractional or negative duration
        // would put the active timeline out of whole milliseconds, and every
        // downstream invariant (Σ visits, Σ entries) is an exact integer test.
        const duration = Math.max(0, Math.trunc(event.duration));
        if (duration === 0) break;
        const step = stepDraft(draft, event.segmentIndex, event.segmentId);
        bankPerformed(
          step,
          draft.openStepIndex === event.segmentIndex,
          draft.performedDuration,
          duration,
        );
        draft.openStepIndex = event.segmentIndex;
        step.performed += duration;
        draft.performedDuration += duration;
        // Presence in `performedBySegment` is itself the claim that the segment was
        // performed, so a key is only ever written with time behind it.
        draft.performedBySegment[event.segmentId] =
          (draft.performedBySegment[event.segmentId] ?? 0) + duration;
        changed = true;
        break;
      }
      case "segmentEnded": {
        const step = stepDraft(draft, event.segmentIndex, event.segmentId);
        if (event.disposition === "completed") {
          step.completed = true;
          record(draft.completedSegmentIds, event.segmentId);
        } else {
          step.skipped = true;
          record(draft.skippedSegmentIds, event.segmentId);
        }
        changed = true;
        break;
      }
      case "segmentBegan": {
        // Occupancy is recorded even when no time follows it, so a step the user
        // genuinely opened is distinguishable from one the session never reached.
        stepDraft(draft, event.segmentIndex, event.segmentId);
        // Entering a step closes whatever was open, including the step itself: a
        // Back that restarts the current interval is a second occupancy of it, not
        // a continuation, even though no other step's time separates the two.
        draft.openStepIndex = undefined;
        changed = true;
        break;
      }
      default:
        break;
    }
  }

  return changed ? sealDraft(draft) : ledger;
}

/**
 * Attaches the guidance a step was performed under.
 *
 * It arrives separately because the engine's `comboCall` event says only that a
 * call is owed — the combination itself is generated by `@ff/combo`, which the
 * engine deliberately does not depend on. Recording it here is what lets the
 * archived entry name the combination the user actually worked rather than
 * reconstructing a plausible one later.
 */
export function withStepGuidance(
  ledger: ExecutionLedger,
  planIndex: number,
  segmentId: SessionSegmentId,
  guidance: ExecutionGuidance,
): ExecutionLedger {
  const draft = draftFrom(ledger);
  stepDraft(draft, planIndex, segmentId).guidance = guidance;
  return sealDraft(draft);
}

/**
 * Rebuilds a full ledger from the flat one a checkpoint persisted.
 *
 * The checkpoint stores `WorkoutExecutionLedger`, which keeps performed totals and
 * earned ids exactly but has nowhere to put visit structure. Reconstructing one
 * contiguous visit per performed segment, laid out in plan order, is therefore the
 * most a resume can honestly claim — and it is enough, because the record's
 * invariants are about totals, monotonicity and non-overlap, all of which this
 * layout satisfies.
 *
 * `performedDuration` is raised to the sum of the per-segment totals when the two
 * disagree and never lowered to it: a checkpoint is untrusted input that must
 * self-heal, and the one direction it may never heal in is downward, because that
 * would delete effort the user already earned.
 */
export function restoredLedger(
  stored: WorkoutExecutionLedger,
  segments: readonly SessionSegment[],
): ExecutionLedger {
  const steps: ExecutionStepLedger[] = [];
  let cursor = 0;

  segments.forEach((segment, planIndex) => {
    const performed = Math.max(0, Math.trunc(stored.performedBySegment[segment.id] ?? 0));
    const completed = stored.completedSegmentIds.includes(segment.id);
    const skipped = stored.skippedSegmentIds.includes(segment.id);
    if (performed === 0 && !completed && !skipped) return;
    steps.push({
      planIndex,
      segmentId: segment.id,
      performed,
      visits:
        performed > 0 ? [{ offsetMilliseconds: cursor, durationMilliseconds: performed }] : [],
      completed,
      skipped,
    });
    cursor += performed;
  });

  const performedBySegment: Record<SessionSegmentId, Milliseconds> = {};
  for (const step of steps) {
    if (step.performed > 0) performedBySegment[step.segmentId] = step.performed;
  }

  return {
    performedDuration: Math.max(Math.max(0, Math.trunc(stored.performedDuration)), cursor),
    performedBySegment,
    completedSegmentIds: [...stored.completedSegmentIds],
    skippedSegmentIds: [...stored.skippedSegmentIds],
    steps,
  };
}

// ---------------------------------------------------------------------------
// Archive projection
// ---------------------------------------------------------------------------

/**
 * The archived disposition of one planned step (F-TR-09, F-TR-10).
 *
 * Skipped is sticky and wins over a later completion: the record must not claim a
 * step was finished cleanly when part of it was abandoned. A step that was never
 * left — the one the session ended inside, and every step the session never
 * reached — is `endedEarly`, which is the only value that describes a step whose
 * planned time was not spent and which was not deliberately dismissed.
 */
function stepDisposition(
  step: ExecutionStepLedger | undefined,
  segment: SessionSegment,
): ExecutionDisposition {
  if (step === undefined) return "endedEarly";
  if (step.skipped) return "skipped";
  if (!step.completed) return "endedEarly";
  // Reps done finishes a step without inventing the time its cap had left, so a
  // completion short of the planned duration is a manual one by definition.
  return step.performed >= segment.duration ? "completedNaturally" : "manualCompletion";
}

export interface ExecutionEntriesInput {
  readonly sessionId: EffortSessionId;
  /** The executed plan's segments, in plan order. */
  readonly segments: readonly SessionSegment[];
  readonly ledger: ExecutionLedger;
  /** The session's measured active duration — the authority the entries reconcile to. */
  readonly activeDuration: Milliseconds;
}

/**
 * Projects the ledger onto the archive's per-step entries (F-TR-38).
 *
 * One entry per *planned* step, including steps the session never reached, so the
 * record describes the whole plan that was executed rather than only its
 * successful prefix. Ids are derived, never minted, so a retry after a failed
 * history write recomputes the same ids and overwrites the first attempt's rows
 * instead of accumulating ghosts.
 *
 * The result is reconciled: the engine measures each step independently and the
 * session total separately, so the two disagree by a few milliseconds on almost
 * every session, and the record's contract is that entry totals sum *exactly* to
 * `activeDurationMilliseconds`. The session clock is the measured authority, so
 * the entries move to it — never the other way round.
 *
 * Dispositions are read from the raw ledger before that reconciliation, because
 * scaling a step's measured time down by two milliseconds must not turn a round
 * the user finished into a manual completion.
 */
export function executionEntries(input: ExecutionEntriesInput): ExecutionEntry[] {
  const byPlanIndex = new Map(input.ledger.steps.map((step) => [step.planIndex, step]));

  const raw = input.segments.map((segment, planIndex): ExecutionEntry => {
    const step = byPlanIndex.get(planIndex);
    // Attribution is positional because the entry id derivation is positional. A
    // step folded against a different plan cannot be reattributed by id, and
    // guessing would move one interval's effort onto another.
    const matched = step !== undefined && step.segmentId === segment.id ? step : undefined;
    return {
      id: deriveExecutionEntryId(input.sessionId, planIndex, segment.id),
      planIndex,
      segmentId: segment.id,
      performedMilliseconds: matched?.performed ?? 0,
      disposition: stepDisposition(matched, segment),
      visits: matched?.visits ?? [],
      ...(matched?.guidance !== undefined ? { guidance: matched.guidance } : {}),
      fidelity: "exact",
    };
  });

  return reconcileExecutionEntries(raw, input.activeDuration);
}
